import React, { useState, useEffect, useRef, useContext } from 'react';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import useI18n from '@/hooks/useI18n';
import { FormDataForCreateCampaign } from '@/types/interfaces';
import { Button } from '../Button';
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';
import IconButton from '../IconButton';
import CustomCheckbox from '../CustomCheckbox';
import TooltipForCashback from '../TooltipForCashback';
import ToolTip from '../ToolTip';
import { UserContext } from '@/context/UserContext';
import { formatPercent } from '@/utils/numberUtils';

interface CreateCampaignStep3Props {
  formData: FormDataForCreateCampaign;
  handleChange: (newValues: Partial<FormDataForCreateCampaign>) => void;
  onNext: () => void;
  onPrevious: () => void;
}

interface TabContentProps {
  cashbackRange: [number, number] | null;
  maxCashback: number | null;
  platforms: string[];
  onCashbackRangeChange: (value: [number, number] | null) => void;
  onMaxCashbackChange: (value: number | null) => void;
  onPlatformChange: (platforms: string[]) => void;
  tabPrefix: 'reel' | 'post' | 'story';
  errors: Partial<Record<keyof FormDataForCreateCampaign, string>>;
  touched: Partial<Record<keyof FormDataForCreateCampaign, boolean>>;
  setTouched: React.Dispatch<
    React.SetStateAction<
      Partial<Record<keyof FormDataForCreateCampaign, boolean>>
    >
  >;
}

type TabPrefix = 'reel' | 'post' | 'story';
type TabStatus = 'empty' | 'partial' | 'complete';

const TabContent: React.FC<TabContentProps> = ({
  cashbackRange,
  maxCashback,
  platforms,
  onCashbackRangeChange,
  onMaxCashbackChange,
  onPlatformChange,
  tabPrefix,
  errors,
  touched,
  setTouched,
}) => {
  const { t } = useI18n();
  const userContext = useContext(UserContext);

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }

  const { user } = userContext;

  const allPlatforms = [
    {
      id: 'instagram',
      icon: '/assets/FullInstagram.svg',
    },
    {
      id: 'tiktok',
      icon: '/assets/FullTikTok.svg',
    },
  ];

  const availablePlatforms =
    tabPrefix === 'story'
      ? allPlatforms.filter((p) => p.id === 'instagram')
      : allPlatforms;

  // Initialize value with [0, 0] if cashbackRange is null or invalid
  const [value, setValue] = useState<[number, number]>(
    cashbackRange && Array.isArray(cashbackRange) && cashbackRange.length === 2
      ? [cashbackRange[0], cashbackRange[1]]
      : [0, 0],
  );

  // State to store thumb positions for tooltip positioning
  const [thumbPositions, setThumbPositions] = useState<[number, number]>([
    0, 0,
  ]);
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Update local value when cashbackRange changes
    if (
      cashbackRange &&
      Array.isArray(cashbackRange) &&
      cashbackRange.length === 2 &&
      typeof cashbackRange[0] === 'number' &&
      typeof cashbackRange[1] === 'number'
    ) {
      setValue([cashbackRange[0], cashbackRange[1]]);
      setThumbPositions([cashbackRange[0], cashbackRange[1]]);
    } else {
      setValue([0, 0]);
      setThumbPositions([0, 0]);
    }
  }, [cashbackRange]);

  const togglePlatform = (platform: string) => {
    let newPlatforms: string[];
    if (platforms.includes(platform)) {
      newPlatforms = platforms.filter((p) => p !== platform);
    } else {
      newPlatforms = [...platforms, platform];
    }
    onPlatformChange(newPlatforms);
    setTouched((prev) => ({ ...prev, [`${tabPrefix}Platforms`]: true }));
  };

  const handleSliderChange = (newValue: [number, number]) => {
    const fixedValue: [number, number] = [0, newValue[1]];
    setValue(fixedValue);
    setThumbPositions(fixedValue);

    if (newValue[1] === 0) {
      onCashbackRangeChange(null);
    } else {
      onCashbackRangeChange(fixedValue);
    }

    setTouched((prev) => ({
      ...prev,
      [`${tabPrefix}CashbackRange`]: true,
    }));
  };

  // Determine if the tooltip should have a selected style
  const isRangeSelected = thumbPositions[1] !== 0;

  return (
    <div className="flex gap-[24px] p-[24px] border-2 border-[#113E53] rounded-b-[24px] border-t-1">
      <div className="w-[40%] flex flex-col justify-between">
        <TooltipForCashback text={String(t('common.cashbackInfo'))}>
          {t('common.selectCashbackRange')}{' '}
        </TooltipForCashback>
        <div
          className={`flex justify-center  ${
            touched[`${tabPrefix}CashbackRange`] &&
            errors[`${tabPrefix}CashbackRange`]
              ? 'mb-0'
              : 'mb-[15px]'
          }`}
        >
          <div className="mt-3 w-[90%]">
            <div className="relative">
              {/* Tooltips above the slider */}
              <div className="absolute w-full -top-8">
                {/* Fixed Tooltip for 0% (Left End) */}
                <div
                  className="absolute"
                  style={{
                    left: '0%',
                    transform: 'translateX(-50%)',
                  }}
                >
                  <span
                    className="text-xs text-black font-normal bg-[#F4F4F4] px-[8px] rounded-full shadow-md"
                    style={{ border: '1px solid #E5E9EB' }}
                  >
                    0%
                  </span>
                </div>
                {/* Fixed Tooltip for 100% (Right End) */}
                <div
                  className="absolute"
                  style={{
                    left: '100%',
                    transform: 'translateX(-50%)',
                  }}
                >
                  <span
                    className="text-xs text-black font-normal bg-[#F4F4F4] px-[8px] rounded-full shadow-md"
                    style={{ border: '1px solid #E5E9EB' }}
                  >
                    100%
                  </span>
                </div>
                <div
                  className="absolute"
                  style={{
                    left: `${(thumbPositions[1] / 100) * 100}%`,
                    transform: 'translateX(-50%)',
                  }}
                >
                  <span
                    className={`text-xs px-[8px] rounded-full shadow-md ${
                      isRangeSelected
                        ? 'bg-[#113E53] text-white font-medium border border-[#113E53]'
                        : 'text-black font-normal bg-[#F4F4F4]'
                    }`}
                  >
                    {formatPercent(thumbPositions[1])}
                  </span>
                </div>
              </div>
              <RangeSlider
                min={0}
                max={100}
                step={1}
                defaultValue={[]}
                value={value}
                onInput={handleSliderChange}
                id="cashback-range-slider"
                className="h-2 rounded-lg cursor-pointer"
                thumbsDisabled={[true, false]}
                thumbs={[0, 1]}
                trackStyle={{
                  background: '#113E53',
                }}
                railStyle={{
                  background: '#E5E7EB',
                }}
                thumbStyle={{
                  background: '#fff',
                  border: '2px solid #113E53',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                }}
                ref={sliderRef}
              />
            </div>
            <div className="text-red-500 text-sm mt-[15px] min-h-[20px]">
              {touched[`${tabPrefix}CashbackRange`] &&
              errors[`${tabPrefix}CashbackRange`] ? (
                errors[`${tabPrefix}CashbackRange`]
              ) : (
                <>&nbsp;</>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="w-[37%]">
        <label className="text-[#172B4D] text-base font-normal flex items-center gap-2">
          {t('common.maximumCashbackPerCreator')}
          <ToolTip
            text="Cap the cashback per creator. Example: if the cap is €20, a creator will never earn more than €20 cashback, even if their social score is higher."
            position="top"
          >
            <span className="text-[#172B4D] cursor-pointer">ⓘ</span>
          </ToolTip>
        </label>
        <div className="relative flex items-center mt-5">
          <input
            type="number"
            value={maxCashback ?? ''}
            onChange={(e) => {
              const value = e.target.value ? Number(e.target.value) : null;
              onMaxCashbackChange(value);
              setTouched((prev) => ({
                ...prev,
                [`${tabPrefix}MaxCashback`]: true,
              }));
            }}
            className="relative block border border-[#B3B3B2] rounded-[32px] bg-white py-3 px-4 text-base text-[#4D4D4D] w-full h-[46px] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none overflow-ellipsis whitespace-nowrap outline-none  focus:border focus:border-[#113E53] text-[#113E53]  leading-[22px] placeholder-[#B3B3B2]"
          />
          <span className="mr-[20px] text-[#94A3B8] text-lg font-normal absolute right-0">
            €
          </span>
        </div>
        <div className="text-red-500 text-sm mt-1 min-h-[20px]">
          {touched[`${tabPrefix}MaxCashback`] &&
          errors[`${tabPrefix}MaxCashback`] ? (
            errors[`${tabPrefix}MaxCashback`]
          ) : (
            <>&nbsp;</>
          )}
        </div>
      </div>
      <div className="">
        <label className="text-[#172B4D] text-base font-normal">
          {t('common.selectcontentPlatform')}
        </label>
        <div className="flex space-x-3 mt-5">
          {availablePlatforms.map((platform) => (
            <IconButton
              key={platform.id}
              onClick={() => togglePlatform(platform.id)}
              className={`px-4 py-2.5 rounded-full bg-white ${
                platforms.includes(platform.id)
                  ? 'border-2 border-[#113E53]'
                  : 'border border-[#B3B3B2]'
              }`}
              type="button"
            >
              <img
                src={platform.icon}
                alt={String(
                  t('campaign.altTexts.platformIcon', {
                    platform: platform.id,
                  }),
                )}
              />
            </IconButton>
          ))}
        </div>
        <div className="text-red-500 text-sm mt-1 min-h-[20px]">
          {touched[`${tabPrefix}Platforms`] &&
          errors[`${tabPrefix}Platforms`] ? (
            errors[`${tabPrefix}Platforms`]
          ) : (
            <>&nbsp;</>
          )}
        </div>
      </div>
    </div>
  );
};

const CreateCampaignStep3: React.FC<CreateCampaignStep3Props> = ({
  formData,
  handleChange,
  onNext,
  onPrevious,
}) => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<'Reel' | 'Post' | 'Story'>('Post');
  const [radioChecked, setRadioChecked] = useState({
    Reel: false,
    Post: false,
    Story: false,
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof FormDataForCreateCampaign, string>>
  >({});
  const [touched, setTouched] = useState<
    Partial<Record<keyof FormDataForCreateCampaign, boolean>>
  >({});

  const isAtLeastOneTabComplete = (): boolean => {
    return (['reel', 'post', 'story'] as TabPrefix[]).some(
      (prefix) => getTabStatus(prefix) === 'complete',
    );
  };
  const hasAnySelection = (): boolean => {
    return (['reel', 'post', 'story'] as TabPrefix[]).some(
      (prefix) => getTabStatus(prefix) !== 'empty',
    );
  };

  const isValidCashbackRange = (
    range?: (number | undefined)[] | null,
  ): boolean => {
    return (
      Array.isArray(range) &&
      range.length === 2 &&
      typeof range[0] === 'number' &&
      typeof range[1] === 'number' &&
      !(range[0] === 0 && range[1] === 0)
    );
  };

  const isValidMaxCashback = (value: number | null | undefined): boolean => {
    return typeof value === 'number' && value > 0;
  };

  const isValidPlatforms = (platforms?: string[]): boolean => {
    return Array.isArray(platforms) && platforms.length > 0;
  };

  const getTabStatus = (prefix: TabPrefix): TabStatus => {
    const range = formData[`${prefix}CashbackRange`];
    const max = formData[`${prefix}MaxCashback`];
    const platforms = formData[`${prefix}Platforms`];

    const hasRange = isValidCashbackRange(range);
    const hasMax = isValidMaxCashback(max);
    const hasPlatforms = isValidPlatforms(platforms);

    const filledCount = [hasRange, hasMax, hasPlatforms].filter(Boolean).length;

    if (filledCount === 0) return 'empty';
    if (filledCount === 3) return 'complete';
    return 'partial';
  };

  // const getTabsToValidate = (): TabPrefix[] => {
  //   return (['reel', 'post', 'story'] as TabPrefix[]).filter(
  //     (prefix) => getTabStatus(prefix) === 'complete',
  //   );
  // };

  const validationSchema = Yup.object({
    reelCashbackRange: Yup.array()
      .of(Yup.number())
      .nullable()
      .test(
        'valid-range',
        String(t('campaign.validation.cashbackRangeRequired')),
        (value) =>
          value == null ||
          (Array.isArray(value) &&
            value.length === 2 &&
            typeof value[0] === 'number' &&
            typeof value[1] === 'number' &&
            !(value[0] === 0 && value[1] === 0)),
      )
      .required(String(t('campaign.validation.cashbackRangeRequired'))),
    reelMaxCashback: Yup.number()
      .typeError(String(t('campaign.validation.maxCashbackMustBeNumber')))
      .required(String(t('campaign.validation.maxCashbackRequired')))
      .test(
        'max-cashback-vs-budget',
        String(t('common.maxCashbackExceedsBudget')),
        function (value) {
          const { budget } = this.parent;

          if (value == null || budget == null) return true;

          return value <= budget;
        },
      ),
    reelPlatforms: Yup.array()
      .min(1, String(t('campaign.validation.platformRequired')))
      .required(String(t('campaign.validation.platformRequired'))),
    postCashbackRange: Yup.array()
      .of(Yup.number())
      .nullable()
      .test(
        'valid-range',
        String(t('campaign.validation.cashbackRangeRequired')),
        (value) =>
          value == null ||
          (Array.isArray(value) &&
            value.length === 2 &&
            typeof value[0] === 'number' &&
            typeof value[1] === 'number' &&
            !(value[0] === 0 && value[1] === 0)),
      )
      .required(String(t('campaign.validation.cashbackRangeRequired'))),

    postMaxCashback: Yup.number()
      .typeError(String(t('campaign.validation.maxCashbackMustBeNumber')))
      .required(String(t('campaign.validation.maxCashbackRequired')))
      .test(
        'max-cashback-vs-budget',
        String(t('common.maxCashbackExceedsBudget')),
        function (value) {
          const { budget } = this.parent;

          if (value == null || budget == null) return true;

          return value <= budget;
        },
      ),
    postPlatforms: Yup.array()
      .min(1, String(t('campaign.validation.platformRequired')))
      .required(String(t('campaign.validation.platformRequired'))),

    storyCashbackRange: Yup.array()
      .of(Yup.number())
      .nullable()
      .test(
        'valid-range',
        String(t('campaign.validation.cashbackRangeRequired')),
        (value) =>
          value == null ||
          (Array.isArray(value) &&
            value.length === 2 &&
            typeof value[0] === 'number' &&
            typeof value[1] === 'number' &&
            !(value[0] === 0 && value[1] === 0)),
      )
      .required(String(t('campaign.validation.cashbackRangeRequired'))),
    storyMaxCashback: Yup.number()
      .typeError(String(t('campaign.validation.maxCashbackMustBeNumber')))
      .required(String(t('campaign.validation.maxCashbackRequired')))
      .test(
        'max-cashback-vs-budget',
        String(t('common.maxCashbackExceedsBudget')),
        function (value) {
          const { budget } = this.parent;

          if (value == null || budget == null) return true;

          return value <= budget;
        },
      ),
    storyPlatforms: Yup.array()
      .min(1, String(t('campaign.validation.platformRequired')))
      .required(String(t('campaign.validation.platformRequired'))),
  });

  const tabs = [
    {
      name: 'Reel',
      subTitle: t('common.reelDesc'),
      icon: '/assets/video.svg',
    },
    {
      name: 'Post',
      subTitle: t('common.postDesc'),
      icon: '/assets/post.svg',
    },
    {
      name: 'Story',
      subTitle: t('common.storyDesc'),
      icon: '/assets/tv.svg',
    },
  ];

  useEffect(() => {
    setRadioChecked({
      Reel: getTabStatus('reel') === 'complete',
      Post: getTabStatus('post') === 'complete',
      Story: getTabStatus('story') === 'complete',
    });
  }, [formData]);
  const isTabCompleteByName = (tabName: 'Reel' | 'Post' | 'Story') => {
    const prefix = tabName.toLowerCase() as TabPrefix;
    return getTabStatus(prefix) === 'complete';
  };

  const handleCheckboxChange = (tabName: 'Reel' | 'Post' | 'Story') => {
    if (isTabCompleteByName(tabName)) return;
    setRadioChecked((prev) => ({
      ...prev,
      [tabName]: false,
    }));
  };
  const handleTabClick = (tabName: 'Reel' | 'Post' | 'Story') => {
    const prefix = tabName.toLowerCase() as 'reel' | 'post' | 'story';
    setActiveTab(tabName);
    setTouched((prev) => ({
      ...prev,
      [`${prefix}CashbackRange`]: true,
      [`${prefix}MaxCashback`]: true,
      [`${prefix}Platforms`]: true,
      'at-least-one-complete': true,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAtLeastOneTabComplete()) {
      const activePrefix = activeTab.toLowerCase() as TabPrefix;

      const activeTabSchema = Yup.object().shape({
        [`${activePrefix}CashbackRange`]:
          validationSchema.fields[`${activePrefix}CashbackRange`],
        [`${activePrefix}MaxCashback`]:
          validationSchema.fields[`${activePrefix}MaxCashback`],
        [`${activePrefix}Platforms`]:
          validationSchema.fields[`${activePrefix}Platforms`],
      });

      try {
        await activeTabSchema.validate(formData, { abortEarly: false });
        setTouched((prev) => ({
          ...prev,
          [`${activePrefix}CashbackRange`]: true,
          [`${activePrefix}MaxCashback`]: true,
          [`${activePrefix}Platforms`]: true,
        }));

        return;
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const newErrors: Partial<
            Record<keyof FormDataForCreateCampaign, string>
          > = {};

          err.inner.forEach((error) => {
            if (error.path) {
              newErrors[error.path as keyof FormDataForCreateCampaign] =
                error.message;
            }
          });

          setErrors(newErrors);

          setTouched((prev) => ({
            ...prev,
            [`${activePrefix}CashbackRange`]: true,
            [`${activePrefix}MaxCashback`]: true,
            [`${activePrefix}Platforms`]: true,
          }));

          toast.error(String(t('campaign.errors.fixErrorsBeforeProceeding')));

          return;
        }
      }
    }

    const submissionData: Partial<FormDataForCreateCampaign> = {
      budget: formData.budget,
    };

    const isValidFinalRange = (range: unknown): range is [number, number] => {
      return (
        Array.isArray(range) &&
        range.length === 2 &&
        Number.isFinite(range[0]) &&
        Number.isFinite(range[1]) &&
        range[0] >= 0 &&
        range[1] <= 100 &&
        !(range[0] === 0 && range[1] === 0)
      );
    };

    const tabs: TabPrefix[] = ['reel', 'post', 'story'];

    // Include only COMPLETE tabs
    tabs.forEach((prefix) => {
      const range = formData[`${prefix}CashbackRange`];
      const max = formData[`${prefix}MaxCashback`];
      const platforms = formData[`${prefix}Platforms`];

      if (getTabStatus(prefix) === 'complete' && isValidFinalRange(range)) {
        submissionData[`${prefix}CashbackRange`] = range;
        submissionData[`${prefix}MaxCashback`] = max;
        submissionData[`${prefix}Platforms`] = platforms;
      } else {
        submissionData[`${prefix}CashbackRange`] = null;
        submissionData[`${prefix}MaxCashback`] = null;
        submissionData[`${prefix}Platforms`] = [];
      }
    });

    // // Find partially filled tabs
    // const partialTabs = tabs.filter(
    //   (prefix) => getTabStatus(prefix) === 'partial',
    // );

    // // Mark partially filled tabs as touched
    // partialTabs.forEach((prefix) => {
    //   setTouched((prev) => ({
    //     ...prev,
    //     [`${prefix}CashbackRange`]: true,
    //     [`${prefix}MaxCashback`]: true,
    //     [`${prefix}Platforms`]: true,
    //   }));
    // });

    try {
      // Validate only complete tabs
      const completeTabs = tabs.filter(
        (prefix) => getTabStatus(prefix) === 'complete',
      );

      const dynamicSchemaFields: Record<string, any> = {
        budget: validationSchema.fields.budget,
      };

      completeTabs.forEach((prefix) => {
        dynamicSchemaFields[`${prefix}CashbackRange`] =
          validationSchema.fields[`${prefix}CashbackRange`];
        dynamicSchemaFields[`${prefix}MaxCashback`] =
          validationSchema.fields[`${prefix}MaxCashback`];
        dynamicSchemaFields[`${prefix}Platforms`] =
          validationSchema.fields[`${prefix}Platforms`];
      });

      const finalSchema = Yup.object().shape(dynamicSchemaFields);

      await finalSchema.validate(submissionData, { abortEarly: false });

      // ✅ No errors
      setErrors({});
      setTouched({});
      handleChange(submissionData);
      onNext();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: Partial<
          Record<keyof FormDataForCreateCampaign, string>
        > = {};

        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path as keyof FormDataForCreateCampaign] =
              error.message;
          }
        });

        setErrors(newErrors);

        // Find first tab with error
        const tabWithError = tabs.find((prefix) =>
          ['CashbackRange', 'MaxCashback', 'Platforms'].some(
            (field) =>
              newErrors[`${prefix}${field}` as keyof FormDataForCreateCampaign],
          ),
        );

        if (tabWithError) {
          setActiveTab(
            (tabWithError.charAt(0).toUpperCase() + tabWithError.slice(1)) as
              | 'Reel'
              | 'Post'
              | 'Story',
          );

          setTouched((prev) => ({
            ...prev,
            [`${tabWithError}CashbackRange`]: true,
            [`${tabWithError}MaxCashback`]: true,
            [`${tabWithError}Platforms`]: true,
          }));
        }

        toast.error(String(t('campaign.errors.fixErrorsBeforeProceeding')));
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
      className="flex flex-col flex-1 min-h-0"
    >
      <div className="flex-1 min-h-0 overflow-y-auto space-y-6 pr-1">
        <h3 className="text-base font-semibold text-[#172B4D]">
          {t('common.chooseContentTypeAndCashback')}{' '}
          <ToolTip text={String(t(`common.step3Tooltip`))} position="top">
            <span>ⓘ</span>
          </ToolTip>
        </h3>
        <div>
          <div className="flex justify-between">
            {tabs.map((tab) => {
              // 1. Calculate how many fields are filled for this specific tab
              const prefix = tab.name.toLowerCase() as TabPrefix;
              const range = formData[`${prefix}CashbackRange`];
              const max = formData[`${prefix}MaxCashback`];
              const platforms = formData[`${prefix}Platforms`];

              const hasRange = isValidCashbackRange(range);
              const hasMax = isValidMaxCashback(max);
              const hasPlatforms = isValidPlatforms(platforms);

              const filledCount = [hasRange, hasMax, hasPlatforms].filter(
                Boolean,
              ).length;
              // const isComplete = filledCount === 3;
              console.log('activeTab', activeTab);
              console.log('tab.name', tab.name);
              const isActive = activeTab === tab.name;
              const isComplete =
                radioChecked[tab.name as 'Reel' | 'Post' | 'Story'];

              return (
                // Inside tabs.map return...
                <div
                  key={tab.name}
                  onClick={() =>
                    handleTabClick(tab.name as 'Reel' | 'Post' | 'Story')
                  }
                  className={`flex gap-[10px] p-[24px] cursor-pointer w-[33.33%] hover:bg-gray-100 hover:rounded-t-[24px] ${
                    activeTab === tab.name
                      ? 'border-t-2 border-l-2 border-r-2 border-[#113E53] border-b-none rounded-t-[24px] -mb-[2px] z-10 bg-white'
                      : 'border-b-1 border-[#E5E9EB]'
                  }`}
                >
                  <CustomCheckbox
                    name={tab.name}
                    value={tab.name}
                    checked={isComplete}
                    step={filledCount}
                    isActive={isActive}
                    onChange={() => handleTabClick(tab.name)}
                    className="mt-[3px]"
                    hasSelection={hasAnySelection()}
                    labelStyle="!items-start"
                  />
                  <div>
                    <img
                      className="h-[24px] w-[24px]"
                      src={tab.icon}
                      alt={String(
                        t('campaign.altTexts.contentTypeIcon', {
                          type: tab.name,
                        }),
                      )}
                    />
                  </div>
                  <div>
                    <div className="text-[#020D3A] text-base">{tab.name}</div>
                    <div className="text-[#020D3A] text-base">
                      {tab.subTitle}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {activeTab === 'Reel' && (
            <TabContent
              tabPrefix="reel"
              cashbackRange={
                Array.isArray(formData.reelCashbackRange) &&
                formData.reelCashbackRange.length === 2 &&
                typeof formData.reelCashbackRange[0] === 'number' &&
                typeof formData.reelCashbackRange[1] === 'number'
                  ? [
                      formData.reelCashbackRange[0],
                      formData.reelCashbackRange[1],
                    ]
                  : null
              }
              maxCashback={formData.reelMaxCashback ?? null}
              platforms={
                formData.reelPlatforms?.includes('both')
                  ? ['instagram', 'tiktok']
                  : formData.reelPlatforms || []
              }
              onCashbackRangeChange={(value) =>
                handleChange({ reelCashbackRange: value })
              }
              onMaxCashbackChange={(value) =>
                handleChange({ reelMaxCashback: value })
              }
              onPlatformChange={(platforms) =>
                handleChange({ reelPlatforms: platforms })
              }
              errors={errors}
              touched={touched}
              setTouched={setTouched}
            />
          )}
          {activeTab === 'Post' && (
            <TabContent
              tabPrefix="post"
              cashbackRange={
                Array.isArray(formData.postCashbackRange) &&
                formData.postCashbackRange.length === 2 &&
                typeof formData.postCashbackRange[0] === 'number' &&
                typeof formData.postCashbackRange[1] === 'number'
                  ? [
                      formData.postCashbackRange[0],
                      formData.postCashbackRange[1],
                    ]
                  : null
              }
              maxCashback={formData.postMaxCashback ?? null}
              platforms={
                formData.postPlatforms?.includes('both')
                  ? ['instagram', 'tiktok']
                  : formData.postPlatforms || []
              }
              onCashbackRangeChange={(value) =>
                handleChange({ postCashbackRange: value })
              }
              onMaxCashbackChange={(value) =>
                handleChange({ postMaxCashback: value })
              }
              onPlatformChange={(platforms) =>
                handleChange({ postPlatforms: platforms })
              }
              errors={errors}
              touched={touched}
              setTouched={setTouched}
            />
          )}
          {activeTab === 'Story' && (
            <TabContent
              tabPrefix="story"
              cashbackRange={
                Array.isArray(formData.storyCashbackRange) &&
                formData.storyCashbackRange.length === 2 &&
                typeof formData.storyCashbackRange[0] === 'number' &&
                typeof formData.storyCashbackRange[1] === 'number'
                  ? [
                      formData.storyCashbackRange[0],
                      formData.storyCashbackRange[1],
                    ]
                  : null
              }
              maxCashback={formData.storyMaxCashback ?? null}
              platforms={
                formData.storyPlatforms?.includes('both')
                  ? ['instagram']
                  : (formData.storyPlatforms || []).filter(
                      (p) => p !== 'tiktok',
                    )
              }
              onCashbackRangeChange={(value) =>
                handleChange({ storyCashbackRange: value })
              }
              onMaxCashbackChange={(value) =>
                handleChange({ storyMaxCashback: value })
              }
              onPlatformChange={(platforms) =>
                handleChange({ storyPlatforms: platforms })
              }
              errors={errors}
              touched={touched}
              setTouched={setTouched}
            />
          )}
        </div>
      </div>
      <div className="bg-white flex justify-end w-full shrink-0">
        <div className="flex gap-4 w-full p-6 justify-end">
          <Button
            variant="outlined"
            type="button"
            onClick={onPrevious}
            className="w-[122px]"
          >
            {t('common.previous')}
          </Button>
          <Button type="submit" className="w-[122px]">
            {t('common.next')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CreateCampaignStep3;
